import React, { FC, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIsMobileDevice } from 'utils';
import * as Styled from './ListView.styles';
import { ListViewItem } from './ListViewItem';
import { sendSeeAllAnalyticsEvent } from '../../helpers/analitycs';
import useInViewAnalytics from '../../hooks/useInViewAnalytics';
import { useSeeAllData } from '../../hooks/useSeeAllData';
import { sortedCollectionsType } from '../../types';

interface ListViewGroupProps {
  collection: sortedCollectionsType;
  onSeeAllClick: (categoryId: string | null) => void;
  selected?: boolean;
}

export const ListViewGroup: FC<ListViewGroupProps> = ({
  collection,
  selected,
  onSeeAllClick,
}) => {
  const [isInView, setIsInView] = React.useState(false);
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: setIsInView,
  });

  const { handleInViewChange } = useInViewAnalytics({
    campaigns: [],
    title: collection.label,
    isInView: selected ? true : isInView,
  });

  const LIMIT_ITEMS_TO_SHOW = selected ? collection.results.length : 3;

  const isMobile = useIsMobileDevice();
  const { contentId, contentType } = useSeeAllData(collection.label);

  const handleSeeAllClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      sendSeeAllAnalyticsEvent(collection.label);
      onSeeAllClick(collection.category_id);
    },
    [onSeeAllClick, collection, contentId, contentType],
  );

  return (
    <Styled.Group ref={inViewRef}>
      {!selected && (
        <Styled.Title variant="h3" fontWeight="600">
          {collection.label}
        </Styled.Title>
      )}
      <Styled.ListViewItems>
        {collection.results
          .slice(0, LIMIT_ITEMS_TO_SHOW)
          .map((campaign, index) => (
            <ListViewItem
              key={campaign._id}
              campaign={campaign}
              sendInViewChange={handleInViewChange}
              collectionTitle={campaign.name}
              index={index}
            />
          ))}
      </Styled.ListViewItems>
      {!selected && collection.results.length > LIMIT_ITEMS_TO_SHOW && (
        <Styled.GroupBottom>
          <Styled.Link
            size={isMobile ? 'small' : 'large'}
            variant="contained"
            color="primary"
            onClick={handleSeeAllClick}
            id={contentId}
            title={contentType}
          >
            See all
          </Styled.Link>
        </Styled.GroupBottom>
      )}
    </Styled.Group>
  );
};
